import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  BookOpen,
  Sparkles,
  ArrowRight,
  CheckCircle2,
  Users,
  Heart,
  GraduationCap,
  Clock,
  Globe,
  Zap,
  TrendingUp,
  Menu,
  X,
  LayoutDashboard
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { LogoFull } from '../components/Logo';
import AnimatedDemo from '../components/AnimatedDemo';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { useLanguage } from '../i18n/LanguageContext';
import { isAuthenticated } from '../services/api';

const LandingPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [isLoggedIn, setIsLoggedIn] = React.useState(false);

  // Check if user is logged in
  React.useEffect(() => {
    setIsLoggedIn(isAuthenticated());
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-purple-50/20 to-white">
      {/* Navigation */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div 
              onClick={() => navigate('/')} 
              className="cursor-pointer hover:opacity-80 transition-opacity"
            >
              <LogoFull iconClassName="w-8 h-8" textClassName="text-xl" />
            </div>

            <div className="hidden md:flex items-center space-x-2">
              <Button 
                variant="ghost" 
                onClick={() => scrollToSection('public')}
                className="text-sm font-medium text-gray-700 hover:text-purple-600 hover:bg-purple-50"
              >
                {t.nav.audience}
              </Button>
              <Button 
                variant="ghost" 
                onClick={() => scrollToSection('comment')}
                className="text-sm font-medium text-gray-700 hover:text-purple-600 hover:bg-purple-50"
              >
                {t.nav.howItWorks}
              </Button>
              <Button 
                variant="ghost" 
                onClick={() => scrollToSection('parcours')}
                className="text-sm font-medium text-gray-700 hover:text-purple-600 hover:bg-purple-50"
              >
                {t.nav.paths}
              </Button>
              <Button 
                variant="ghost" 
                onClick={() => scrollToSection('faq')}
                className="text-sm font-medium text-gray-700 hover:text-purple-600 hover:bg-purple-50"
              >
                {t.nav.faq}
              </Button>
              <Button 
                variant="ghost" 
                onClick={() => navigate('/pricing')}
                className="text-sm font-medium text-gray-700 hover:text-purple-600 hover:bg-purple-50"
              >
                {t.nav.pricing}
              </Button>
            </div>

            <div className="flex items-center space-x-3">
              <LanguageSwitcher />
              
              {isLoggedIn ? (
                /* Bouton Dashboard pour utilisateur connecté */
                <Button
                  className="hidden md:inline-flex bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white shadow-lg hover:shadow-xl transition-all"
                  onClick={() => navigate('/dashboard')}
                >
                  <LayoutDashboard className="w-4 h-4 mr-2" />
                  {t.nav.myDashboard}
                </Button>
              ) : (
                <>
                  {/* Bouton Se connecter */}
                  <Button
                    variant="outline"
                    className="hidden md:inline-flex border-purple-600 text-purple-600 hover:bg-purple-50"
                    onClick={() => navigate('/login')}
                  >
                    {t.auth.login}
                  </Button>
                  
                  {/* Bouton Commencer gratuitement */}
                  <Button
                    className="hidden md:inline-flex bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white shadow-lg"
                    onClick={() => navigate('/signup')}
                  >
                    {t.nav.startFree}
                  </Button>
                </>
              )}

              {/* Mobile menu button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 space-y-2 border-t border-gray-100 bg-white">
              <Button 
                variant="ghost" 
                onClick={() => scrollToSection('public')}
                className="w-full justify-start text-gray-700 hover:text-purple-600 hover:bg-purple-50"
              >
                {t.nav.audience}
              </Button>
              <Button 
                variant="ghost" 
                onClick={() => scrollToSection('comment')}
                className="w-full justify-start text-gray-700 hover:text-purple-600 hover:bg-purple-50"
              >
                {t.nav.howItWorks}
              </Button>
              <Button 
                variant="ghost" 
                onClick={() => scrollToSection('parcours')}
                className="w-full justify-start text-gray-700 hover:text-purple-600 hover:bg-purple-50"
              >
                {t.nav.paths}
              </Button>
              <Button 
                variant="ghost" 
                onClick={() => scrollToSection('faq')}
                className="w-full justify-start text-gray-700 hover:text-purple-600 hover:bg-purple-50"
              >
                {t.nav.faq}
              </Button>
              <Button 
                variant="ghost" 
                onClick={() => {
                  navigate('/pricing');
                  setMobileMenuOpen(false);
                }}
                className="w-full justify-start text-gray-700 hover:text-purple-600 hover:bg-purple-50"
              >
                {t.nav.pricing}
              </Button>
              <div className="px-4 pt-2 space-y-2">
                {isLoggedIn ? (
                  <Button
                    className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white flex items-center justify-center space-x-2"
                    onClick={() => navigate('/dashboard')}
                  >
                    <LayoutDashboard className="w-4 h-4" />
                    <span>{t.nav.myDashboard}</span>
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      className="w-full border-purple-600 text-purple-600 hover:bg-purple-50"
                      onClick={() => navigate('/login')}
                    >
                      {t.auth.login}
                    </Button>
                    <Button
                      className="w-full bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white"
                      onClick={() => navigate('/signup')}
                    >
                      {t.nav.startFree}
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </nav>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 md:py-32">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 via-transparent to-amber-500/10" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 animate-fade-in">
              <Badge className="bg-purple-50 text-purple-700 hover:bg-purple-100 border-purple-200 text-base px-4 py-1">
                <Sparkles className="w-4 h-4 mr-2" />
                {t.hero.badge}
              </Badge>

              <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold leading-tight">
                {t.hero.title}{' '}
                <span className="bg-gradient-to-r from-purple-600 via-violet-600 to-indigo-600 bg-clip-text text-transparent">{t.hero.titleHighlight}</span>
              </h1>

              <p className="text-xl text-gray-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: t.hero.subtitle }}></p>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="flex items-start space-x-3">
                  <CheckCircle2 className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: t.hero.feature1 }}></span>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle2 className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: t.hero.feature2 }}></span>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle2 className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: t.hero.feature3 }}></span>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle2 className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: t.hero.feature4 }}></span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  size="lg"
                  className="bg-purple-600 hover:bg-purple-700 text-white text-lg px-8 py-6 shadow-xl hover:shadow-2xl transition-all"
                  onClick={() => navigate('/signup')}
                >
                  {t.hero.cta}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-purple-600 text-purple-600 hover:bg-purple-50 text-lg px-8 py-6"
                  onClick={() => scrollToSection('comment')}
                >
                  {t.hero.ctaSecondary}
                </Button>
              </div>

              <p className="text-sm text-gray-500">
                {t.hero.noCommitment}
              </p>
            </div>

            {/* Hero Image - Religious symbols */}
            <div className="relative animate-slide-in">
              <div className="grid grid-cols-2 gap-4">
                <img
                  src="https://images.unsplash.com/photo-1590273089302-ebbc53986b6e?w=400"
                  alt="Mosquée"
                  className="rounded-xl shadow-xl hover:scale-105 transition-transform duration-300 w-full h-48 object-cover"
                />
                <img
                  src="https://images.unsplash.com/photo-1515162305285-0293e4767cc2?w=400"
                  alt="Église"
                  className="rounded-xl shadow-xl hover:scale-105 transition-transform duration-300 w-full h-48 object-cover"
                />
                <img
                  src="https://images.unsplash.com/photo-1585834372646-5306d75a60ee?w=400"
                  alt="Synagogue"
                  className="rounded-xl shadow-xl hover:scale-105 transition-transform duration-300 w-full h-48 object-cover"
                />
                <img
                  src="https://images.unsplash.com/photo-1655815917186-76a5e56b58d5?w=400"
                  alt="Temple Bouddhiste"
                  className="rounded-xl shadow-xl hover:scale-105 transition-transform duration-300 w-full h-48 object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* À qui s'adresse */}
      <section id="public" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">{t.audience.title}</h2>
            <p className="text-lg text-gray-600">
              {t.audience.subtitle}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="hover-lift border-0 shadow-lg">
              <CardHeader>
                <div className="w-12 h-12 rounded-full bg-purple-50 flex items-center justify-center mb-4">
                  <Users className="w-6 h-6 text-purple-600" />
                </div>
                <CardTitle className="text-xl">{t.audience.card1Title}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  {t.audience.card1Text}
                </p>
              </CardContent>
            </Card>

            <Card className="hover-lift border-0 shadow-lg">
              <CardHeader>
                <div className="w-12 h-12 rounded-full bg-purple-50 flex items-center justify-center mb-4">
                  <Heart className="w-6 h-6 text-purple-600" />
                </div>
                <CardTitle className="text-xl">{t.audience.card2Title}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  {t.audience.card2Text}
                </p>
              </CardContent>
            </Card>

            <Card className="hover-lift border-0 shadow-lg">
              <CardHeader>
                <div className="w-12 h-12 rounded-full bg-purple-50 flex items-center justify-center mb-4">
                  <GraduationCap className="w-6 h-6 text-purple-600" />
                </div>
                <CardTitle className="text-xl">{t.audience.card3Title}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  {t.audience.card3Text}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Comment ça marche */}
      <section id="comment" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-purple-50/30 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">{t.howItWorks.title}</h2>
            <p className="text-lg text-gray-600">
              {t.howItWorks.subtitle}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="hover-lift border-0 shadow-lg relative">
              <div className="absolute -top-4 -left-4 w-12 h-12 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold text-lg shadow-lg">
                1
              </div>
              <CardHeader className="pt-8">
                <CardTitle className="text-xl">{t.howItWorks.step1Title}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  {t.howItWorks.step1Text}
                </p>
              </CardContent>
            </Card>

            <Card className="hover-lift border-0 shadow-lg relative">
              <div className="absolute -top-4 -left-4 w-12 h-12 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold text-lg shadow-lg">
                2
              </div>
              <CardHeader className="pt-8">
                <CardTitle className="text-xl">{t.howItWorks.step2Title}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  {t.howItWorks.step2Text}
                </p>
              </CardContent>
            </Card>

            <Card className="hover-lift border-0 shadow-lg relative">
              <div className="absolute -top-4 -left-4 w-12 h-12 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold text-lg shadow-lg">
                3
              </div>
              <CardHeader className="pt-8">
                <CardTitle className="text-xl">{t.howItWorks.step3Title}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  {t.howItWorks.step3Text}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Video Demo Section */}
          <div className="mt-16 max-w-5xl mx-auto">
            <div className="text-center mb-8">
              <Badge className="bg-purple-50 text-purple-700 hover:bg-purple-100 border-purple-200 text-base px-4 py-1 mb-4">
                <Sparkles className="w-4 h-4 mr-2" />
                {t.howItWorks.videoBadge}
              </Badge>
              <h3 className="text-2xl md:text-3xl font-bold mb-3">
                {t.howItWorks.videoTitle}
              </h3>
              <p className="text-gray-600">
                {t.howItWorks.videoSubtitle}
              </p>
            </div>

            {/* Animated Interactive Demo */}
            <AnimatedDemo />

            {/* Benefits under video */}
            <div className="grid md:grid-cols-3 gap-6 mt-8">
              <div className="flex items-start space-x-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h5 className="font-semibold text-gray-900 mb-1">{t.howItWorks.benefit1}</h5>
                  <p className="text-sm text-gray-600">{t.howItWorks.benefit1Sub}</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <Zap className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h5 className="font-semibold text-gray-900 mb-1">{t.howItWorks.benefit2}</h5>
                  <p className="text-sm text-gray-600">{t.howItWorks.benefit2Sub}</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h5 className="font-semibold text-gray-900 mb-1">{t.howItWorks.benefit3}</h5>
                  <p className="text-sm text-gray-600">{t.howItWorks.benefit3Sub}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Parcours */}
      <section id="parcours" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">{t.paths.title}</h2>
            <p className="text-lg text-gray-600">
              {t.paths.subtitle}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {/* Islam */}
            <Card className="hover-lift border-0 shadow-lg">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-2xl">☪️</span>
                  <Badge className="w-fit" variant="outline">{t.paths.badge}</Badge>
                </div>
                <CardTitle className="text-lg">{t.paths.islam}</CardTitle>
                <CardDescription className="text-sm">
                  {t.paths.islamDesc}
                </CardDescription>
              </CardHeader>
            </Card>

            {/* Christianisme */}
            <Card className="hover-lift border-0 shadow-lg">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-2xl">✝️</span>
                  <Badge className="w-fit" variant="outline">{t.paths.badge}</Badge>
                </div>
                <CardTitle className="text-lg">{t.paths.christianity}</CardTitle>
                <CardDescription className="text-sm">
                  {t.paths.christianityDesc}
                </CardDescription>
              </CardHeader>
            </Card>

            {/* Judaïsme */}
            <Card className="hover-lift border-0 shadow-lg">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-2xl">✡️</span>
                  <Badge className="w-fit" variant="outline">{t.paths.badge}</Badge>
                </div>
                <CardTitle className="text-lg">{t.paths.judaism}</CardTitle>
                <CardDescription className="text-sm">
                  {t.paths.judaismDesc}
                </CardDescription>
              </CardHeader>
            </Card>

            {/* Spiritualité */}
            <Card className="hover-lift border-0 shadow-lg">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-2xl">🌟</span>
                  <Badge className="w-fit" variant="outline">{t.paths.badge}</Badge>
                </div>
                <CardTitle className="text-lg">{t.paths.spirituality}</CardTitle>
                <CardDescription className="text-sm">
                  {t.paths.spiritualityDesc}
                </CardDescription>
              </CardHeader>
            </Card>

            {/* Bouddhisme - NEW */}
            <Card className="hover-lift border-0 shadow-lg ring-2 ring-purple-200">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-2xl">☸️</span>
                  <Badge className="w-fit bg-purple-100 text-purple-700">{t.paths.newBadge}</Badge>
                </div>
                <CardTitle className="text-lg">{t.paths.buddhism}</CardTitle>
                <CardDescription className="text-sm">
                  {t.paths.buddhismDesc}
                </CardDescription>
              </CardHeader>
            </Card>

            {/* Hindouisme - NEW */}
            <Card className="hover-lift border-0 shadow-lg ring-2 ring-purple-200">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-2xl">🕉️</span>
                  <Badge className="w-fit bg-purple-100 text-purple-700">{t.paths.newBadge}</Badge>
                </div>
                <CardTitle className="text-lg">{t.paths.hinduism}</CardTitle>
                <CardDescription className="text-sm">
                  {t.paths.hinduismDesc}
                </CardDescription>
              </CardHeader>
            </Card>

            {/* Taoïsme - NEW */}
            <Card className="hover-lift border-0 shadow-lg ring-2 ring-purple-200">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-2xl">☯️</span>
                  <Badge className="w-fit bg-purple-100 text-purple-700">{t.paths.newBadge}</Badge>
                </div>
                <CardTitle className="text-lg">{t.paths.taoism}</CardTitle>
                <CardDescription className="text-sm">
                  {t.paths.taoismDesc}
                </CardDescription>
              </CardHeader>
            </Card>

            {/* Transversal - NEW */}
            <Card className="hover-lift border-0 shadow-lg ring-2 ring-cyan-200">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-2xl">🌐</span>
                  <Badge className="w-fit bg-cyan-100 text-cyan-700">{t.paths.newBadge}</Badge>
                </div>
                <CardTitle className="text-lg">{t.paths.transversal}</CardTitle>
                <CardDescription className="text-sm">
                  {t.paths.transversalDesc}
                </CardDescription>
              </CardHeader>
            </Card>
          </div>

          <p className="text-center text-gray-600 mt-8 max-w-3xl mx-auto">
            {t.paths.approach}
          </p>
        </div>
      </section>

      {/* Fonctionnalités */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-purple-50/30 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">{t.features.title}</h2>
            <p className="text-lg text-gray-600">
              {t.features.subtitle}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card className="hover-lift border-0 shadow-md">
              <CardHeader>
                <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center mb-3">
                  <Clock className="w-5 h-5 text-purple-600" />
                </div>
                <CardTitle className="text-lg">{t.features.microLessons}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600">
                  {t.features.microLessonsText}
                </p>
              </CardContent>
            </Card>

            <Card className="hover-lift border-0 shadow-md">
              <CardHeader>
                <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center mb-3">
                  <CheckCircle2 className="w-5 h-5 text-purple-600" />
                </div>
                <CardTitle className="text-lg">{t.features.quizzes}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600">
                  {t.features.quizzesText}
                </p>
              </CardContent>
            </Card>

            <Card className="hover-lift border-0 shadow-md">
              <CardHeader>
                <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center mb-3">
                  <Globe className="w-5 h-5 text-purple-600" />
                </div>
                <CardTitle className="text-lg">{t.features.comparative}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600">
                  {t.features.comparativeText}
                </p>
              </CardContent>
            </Card>

            <Card className="hover-lift border-0 shadow-md">
              <CardHeader>
                <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center mb-3">
                  <Zap className="w-5 h-5 text-purple-600" />
                </div>
                <CardTitle className="text-lg">{t.features.gamification}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600">
                  {t.features.gamificationText}
                </p>
              </CardContent>
            </Card>

            <Card className="hover-lift border-0 shadow-md">
              <CardHeader>
                <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center mb-3">
                  <TrendingUp className="w-5 h-5 text-purple-600" />
                </div>
                <CardTitle className="text-lg">{t.features.progress}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600">
                  {t.features.progressText}
                </p>
              </CardContent>
            </Card>

            <Card className="hover-lift border-0 shadow-md">
              <CardHeader>
                <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center mb-3">
                  <BookOpen className="w-5 h-5 text-purple-600" />
                </div>
                <CardTitle className="text-lg">{t.features.revision}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600">
                  {t.features.revisionText}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">{t.faq.title}</h2>
            <p className="text-lg text-gray-600">
              {t.faq.subtitle}
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem 
              value="item-1"
              className="bg-white rounded-lg shadow-md border-0 px-6"
            >
              <AccordionTrigger className="text-left font-semibold hover:text-purple-600 py-5">
                {t.faq.q1}
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 pb-5">
                {t.faq.a1}
              </AccordionContent>
            </AccordionItem>

            <AccordionItem 
              value="item-2"
              className="bg-white rounded-lg shadow-md border-0 px-6"
            >
              <AccordionTrigger className="text-left font-semibold hover:text-purple-600 py-5">
                {t.faq.q2}
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 pb-5">
                {t.faq.a2}
              </AccordionContent>
            </AccordionItem>

            <AccordionItem 
              value="item-3"
              className="bg-white rounded-lg shadow-md border-0 px-6"
            >
              <AccordionTrigger className="text-left font-semibold hover:text-purple-600 py-5">
                {t.faq.q3}
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 pb-5">
                {t.faq.a3}
              </AccordionContent>
            </AccordionItem>

            <AccordionItem 
              value="item-4"
              className="bg-white rounded-lg shadow-md border-0 px-6"
            >
              <AccordionTrigger className="text-left font-semibold hover:text-purple-600 py-5">
                {t.faq.q4}
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 pb-5">
                {t.faq.a4}
              </AccordionContent>
            </AccordionItem>

            <AccordionItem 
              value="item-5"
              className="bg-white rounded-lg shadow-md border-0 px-6"
            >
              <AccordionTrigger className="text-left font-semibold hover:text-purple-600 py-5">
                {t.faq.q5}
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 pb-5">
                {t.faq.a5}
              </AccordionContent>
            </AccordionItem>

            <AccordionItem 
              value="item-6"
              className="bg-white rounded-lg shadow-md border-0 px-6"
            >
              <AccordionTrigger className="text-left font-semibold hover:text-purple-600 py-5">
                {t.faq.q6}
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 pb-5">
                {t.faq.a6}
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem
              value="item-7"
              className="bg-white rounded-lg shadow-md border-0 px-6"
            >
              <AccordionTrigger className="text-left font-semibold hover:text-purple-600 py-5">
                {t.faq.q7}
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 pb-5">
                {t.faq.a7}
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem
              value="item-8"
              className="bg-white rounded-lg shadow-md border-0 px-6"
            >
              <AccordionTrigger className="text-left font-semibold hover:text-purple-600 py-5">
                {t.faq.q8}
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 pb-5">
                {t.faq.a8}
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-purple-600 via-purple-700 to-indigo-700">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            {t.cta.title}
          </h2>
          <p className="text-xl mb-8 opacity-90">
            {t.cta.subtitle}
          </p>
          <Button
            size="lg"
            className="bg-white text-purple-700 hover:bg-gray-100 text-lg px-12 py-6 shadow-2xl hover:scale-105 transition-transform"
            onClick={() => navigate('/signup')}
          >
            {t.cta.button}
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          <p className="mt-4 text-sm opacity-75">{t.cta.free}</p>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white to-purple-50/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-purple-100 text-purple-700 hover:bg-purple-100">
              {t.testimonials?.badge || "Témoignages"}
            </Badge>
            <h2 className="text-4xl sm:text-5xl font-bold mb-4">
              {t.testimonials?.title || "Ce qu'ils disent de Spirify"}
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {t.testimonials?.subtitle || "Des milliers d'utilisateurs découvrent les religions avec Spirify"}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Testimonial 1 */}
            <Card className="bg-white shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-center mb-4">
                  <div className="flex text-amber-400">
                    {"★".repeat(5)}
                  </div>
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "{t.testimonials?.testimonial1Text || "Une approche neutre et pédagogique qui m'a permis de mieux comprendre l'Islam. Les leçons sont courtes et très bien structurées."}"
                </p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 font-bold text-lg mr-3">
                    {t.testimonials?.testimonial1Name?.[0] || "M"}
                  </div>
                  <div>
                    <p className="font-semibold">{t.testimonials?.testimonial1Name || "Marie L."}</p>
                    <p className="text-sm text-gray-500">{t.testimonials?.testimonial1Role || "Enseignante"}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Testimonial 2 */}
            <Card className="bg-white shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-center mb-4">
                  <div className="flex text-amber-400">
                    {"★".repeat(5)}
                  </div>
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "{t.testimonials?.testimonial2Text || "En tant que musulman, j'ai apprécié redécouvrir ma religion et explorer le christianisme et le judaïsme. Spirify favorise vraiment le dialogue."}"
                </p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold text-lg mr-3">
                    {t.testimonials?.testimonial2Name?.[0] || "A"}
                  </div>
                  <div>
                    <p className="font-semibold">{t.testimonials?.testimonial2Name || "Ahmed K."}</p>
                    <p className="text-sm text-gray-500">{t.testimonials?.testimonial2Role || "Étudiant"}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Testimonial 3 */}
            <Card className="bg-white shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-center mb-4">
                  <div className="flex text-amber-400">
                    {"★".repeat(5)}
                  </div>
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "{t.testimonials?.testimonial3Text || "Je voulais comprendre les religions sans me sentir jugée. Spirify est parfait pour ça : neutre, respectueux et ludique !"}"
                </p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center text-pink-600 font-bold text-lg mr-3">
                    {t.testimonials?.testimonial3Name?.[0] || "S"}
                  </div>
                  <div>
                    <p className="font-semibold">{t.testimonials?.testimonial3Name || "Sophie B."}</p>
                    <p className="text-sm text-gray-500">{t.testimonials?.testimonial3Role || "Designer"}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Testimonial 4 */}
            <Card className="bg-white shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-center mb-4">
                  <div className="flex text-amber-400">
                    {"★".repeat(5)}
                  </div>
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "{t.testimonials?.testimonial4Text || "Parfait pour mes cours d'histoire des religions. Mes élèves adorent l'approche ludique et objective de Spirify."}"
                </p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center text-green-600 font-bold text-lg mr-3">
                    {t.testimonials?.testimonial4Name?.[0] || "J"}
                  </div>
                  <div>
                    <p className="font-semibold">{t.testimonials?.testimonial4Name || "Jean P."}</p>
                    <p className="text-sm text-gray-500">{t.testimonials?.testimonial4Role || "Professeur d'histoire"}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Testimonial 5 */}
            <Card className="bg-white shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-center mb-4">
                  <div className="flex text-amber-400">
                    {"★".repeat(5)}
                  </div>
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "{t.testimonials?.testimonial5Text || "En tant que juive, j'ai pu mieux comprendre l'histoire de ma religion et découvrir les autres traditions abrahamiques. Une vraie richesse !"}"
                </p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 font-bold text-lg mr-3">
                    {t.testimonials?.testimonial5Name?.[0] || "R"}
                  </div>
                  <div>
                    <p className="font-semibold">{t.testimonials?.testimonial5Name || "Rachel M."}</p>
                    <p className="text-sm text-gray-500">{t.testimonials?.testimonial5Role || "Médecin"}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Testimonial 6 */}
            <Card className="bg-white shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-center mb-4">
                  <div className="flex text-amber-400">
                    {"★".repeat(5)}
                  </div>
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "{t.testimonials?.testimonial6Text || "Application géniale pour comprendre le monde qui nous entoure. Les leçons sont courtes, parfaites pour mon emploi du temps chargé."}"
                </p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 font-bold text-lg mr-3">
                    {t.testimonials?.testimonial6Name?.[0] || "T"}
                  </div>
                  <div>
                    <p className="font-semibold">{t.testimonials?.testimonial6Name || "Thomas D."}</p>
                    <p className="text-sm text-gray-500">{t.testimonials?.testimonial6Role || "Ingénieur"}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-16 text-center">
            <div>
              <div className="text-4xl font-bold text-purple-600 mb-2">10K+</div>
              <div className="text-gray-600">{t.testimonials?.stat1 || "Utilisateurs actifs"}</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-purple-600 mb-2">260+</div>
              <div className="text-gray-600">{t.testimonials?.stat2 || "Leçons disponibles"}</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-purple-600 mb-2">4.8/5</div>
              <div className="text-gray-600">{t.testimonials?.stat3 || "Note moyenne"}</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-purple-600 mb-2">7</div>
              <div className="text-gray-600">{t.testimonials?.stat4 || "Religions & Spiritualités"}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <LogoFull iconClassName="w-7 h-7" textClassName="text-lg" />

            <p className="text-sm text-gray-600 text-center">
              {t.footer.copyright}
            </p>

            <div className="flex items-center space-x-6 text-sm text-gray-600">
              <button 
                onClick={() => navigate('/contact')}
                className="hover:text-purple-600 transition-colors cursor-pointer"
              >
                {t.footer.contact}
              </button>
              <button 
                onClick={() => navigate('/privacy')}
                className="hover:text-purple-600 transition-colors cursor-pointer"
              >
                {t.footer.privacy || 'Confidentialité'}
              </button>
              <button 
                onClick={() => navigate('/legal')}
                className="hover:text-purple-600 transition-colors cursor-pointer"
              >
                {t.footer.legal}
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;